import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import CommentThread from './components/CommentThread';
import ClapSystem from './components/ClapSystem';
import CommunityInsights from './components/CommunityInsights';
import SocialShare from './components/SocialShare';
import articlesService from '../../services/articlesService';
import engagementService from '../../services/engagementService';
import { useAuth } from '../../contexts/AuthContext';
import { toArabicDigits } from '../../utils/numbers';

const ArticleEngagementHub = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('comments');

  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasClapped, setHasClapped] = useState(false);
  const [clapCount, setClapCount] = useState(0);
  const [clapLoading, setClapLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const fetchArticle = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await articlesService.getArticle(id);
      setArticle(data);
      setHasClapped(data.hasUserClapped || false);
      setClapCount(data.claps || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل المقال.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchComments = useCallback(async () => {
    if (!id) return;
    setCommentsLoading(true);
    try {
      const { data } = await engagementService.getComments(id);
      setComments(data || []);
    } catch {
      // Silently fail
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleClap = async () => {
    if (!id || clapLoading) return;
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    setClapLoading(true);
    try {
      if (hasClapped) {
        const { data } = await engagementService.removeClap(id);
        setHasClapped(false);
        setClapCount(data?.claps ?? clapCount - 1);
      } else {
        const { data } = await engagementService.clap(id);
        setHasClapped(true);
        setClapCount(data?.claps ?? clapCount + 1);
      }
    } catch (err) {
      console.error('Clap failed:', err);
    } finally {
      setClapLoading(false);
    }
  };

  const handleCommentSubmit = async (content) => {
    if (!id || !content?.trim()) return;
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    try {
      await engagementService.addComment(id, { content: content.trim() });
      await fetchComments();
    } catch (err) {
      console.error('Comment failed:', err);
      throw err;
    }
  };

  const handleReplySubmit = async (parentCommentId, content) => {
    if (!id || !content?.trim()) return;
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    try {
      await engagementService.addComment(id, {
        content: content.trim(),
        parentCommentId
      });
      await fetchComments();
    } catch (err) {
      console.error('Reply failed:', err);
      throw err;
    }
  };

  const handleLikeComment = async (commentId, hasUserLiked) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    const delta = hasUserLiked ? -1 : 1;
    const newLiked = !hasUserLiked;
    const updateInTree = (c) => {
      if (c.id === commentId) {
        return { ...c, likeCount: (c.likeCount ?? 0) + delta, hasUserLiked: newLiked };
      }
      return { ...c, replies: (c.replies ?? []).map(updateInTree) };
    };
    setComments((prev) => prev.map(updateInTree));
    try {
      if (hasUserLiked) {
        await engagementService.removeCommentLike(commentId);
      } else {
        await engagementService.likeComment(commentId);
      }
      await fetchComments();
    } catch (err) {
      console.error('Like failed:', err);
      await fetchComments();
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await engagementService.deleteComment(commentId);
      await fetchComments();
    } catch (err) {
      console.error('Delete comment failed:', err);
      throw err;
    }
  };

  const handleEditComment = async (commentId, content) => {
    try {
      await engagementService.updateComment(commentId, content);
      await fetchComments();
    } catch (err) {
      console.error('Edit comment failed:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-center" style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}>
          جاري التحميل...
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-center">
          <p className="mb-4" style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}>
            {error || 'المقال غير موجود'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 rounded-full"
            style={{ backgroundColor: '#C5A059', color: 'white', fontFamily: 'Amiri, serif' }}
          >
            العودة
          </button>
        </div>
      </div>
    );
  }

  const insights = {
    totalReads: article.views ?? 0,
    avgReadTime: article.readTime || '0 دقائق',
    completionRate: null,
    shares: article.shareCount ?? 0
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <button
        onClick={() => navigate(`/article/${id}`)}
        className="fixed top-10 right-10 z-40 w-14 h-14 rounded-full bg-[rgba(197,160,89,0.1)] hover:bg-[rgba(197,160,89,0.2)] transition-all duration-300 flex items-center justify-center group"
        aria-label="العودة"
      >
        <Icon name="ArrowRight" size={24} color="#C5A059" />
      </button>

      <main className="max-w-[900px] mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2
            className="text-4xl mb-4"
            style={{
              fontFamily: 'Aref Ruqaa, serif',
              color: '#2C2B29',
              fontWeight: 700
            }}
          >
            تفاعل مع المجتمع
          </h2>
          <p
            className="text-lg"
            style={{
              fontFamily: 'Amiri, serif',
              color: '#5A5A5A'
            }}
          >
            شارك رأيك وتفاعل مع القراء الآخرين
          </p>
        </div>

        <div className="mb-12">
          <ClapSystem
            articleId={id}
            totalClaps={clapCount}
            hasClapped={hasClapped}
            onClap={handleClap}
            loading={clapLoading}
            isAuthenticated={isAuthenticated}
          />
        </div>

        <div className="mb-16">
          <SocialShare articleTitle={article.title} articleId={id} />
        </div>

        <div className="w-full h-[1px] bg-[rgba(197,160,89,0.2)] mb-12" />

        <div className="flex items-center justify-center gap-4 mb-8">
          <button
            onClick={() => setActiveTab('comments')}
            className={`px-6 py-3 rounded-full transition-all duration-300 ${
              activeTab === 'comments'
                ? 'bg-[#C5A059] text-white shadow-warm-lg'
                : 'bg-[rgba(197,160,89,0.1)] text-[#C5A059] hover:bg-[rgba(197,160,89,0.15)]'
            }`}
            style={{ fontFamily: 'Amiri, serif', fontSize: '16px', fontWeight: 600 }}
          >
            <div className="flex items-center gap-2">
              <Icon
                name="MessageCircle"
                size={18}
                color={activeTab === 'comments' ? 'white' : '#C5A059'}
              />
              <span>التعليقات ({toArabicDigits(comments?.length ?? 0)})</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('insights')}
            className={`px-6 py-3 rounded-full transition-all duration-300 ${
              activeTab === 'insights'
                ? 'bg-[#C5A059] text-white shadow-warm-lg'
                : 'bg-[rgba(197,160,89,0.1)] text-[#C5A059] hover:bg-[rgba(197,160,89,0.15)]'
            }`}
            style={{ fontFamily: 'Amiri, serif', fontSize: '16px', fontWeight: 600 }}
          >
            <div className="flex items-center gap-2">
              <Icon
                name="BarChart3"
                size={18}
                color={activeTab === 'insights' ? 'white' : '#C5A059'}
              />
              <span>إحصائيات المجتمع</span>
            </div>
          </button>
        </div>

        {activeTab === 'comments' ? (
          <div>
            <CommentThread
              comments={comments}
              loading={commentsLoading}
              onCommentSubmit={handleCommentSubmit}
              onReplySubmit={handleReplySubmit}
              onDeleteComment={handleDeleteComment}
              onEditComment={handleEditComment}
              onLikeComment={handleLikeComment}
              isAuthenticated={isAuthenticated}
              currentUserId={user?.id}
              isAdmin={isAdmin}
            />
          </div>
        ) : (
          <div>
            <CommunityInsights insights={insights} />
          </div>
        )}
      </main>
    </div>
  );
};

export default ArticleEngagementHub;
